<script setup>
import { computed, ref } from "vue";
import { useToastStore } from "@/stores/toast.js";
import { useModalogStore } from "@/stores/modalog";
import { router } from "@inertiajs/vue3";
import { useApiService } from "@/services/useApiService";
import BrandConfirmModalog from "@/Components/Modalogs/BrandConfirmModalog.vue";
import IconHint from "~/svg/icon-hint.svg";

// Props
const props = defineProps({
    action: {
        type: String,
        required: true,
        validator: function (value) {
            return ["block", "unblock"].indexOf(value) !== -1;
        },
    },
    redirectAfterBlocking: {
        type: Boolean,
        default: false,
    },
    user: {
        type: Object,
        default: null,
    },
});

// State
const isSubmitting = ref(false);

// Stores
const modalogStore = useModalogStore();
const toastStore = useToastStore();

// Services
const { blockUser, unblockUser } = useApiService();

// Computed
const actionToUppercase = computed(() => {
    return props.action.charAt(0).toUpperCase() + props.action.slice(1);
});

const isBlocked = computed(() => {
    return props.user.hasOwnProperty("is_blocked")
        ? props.user.is_blocked
        : null;
});

// Methods
function handleClick() {
    isSubmitting.value = true;

    if (isBlocked.value) {
        // Unblock
        unblockUser(props.user.uuid)
            .then(() => {
                modalogStore.closeModalog();

                toastStore.success(
                    `${props.user.presentation_name} is no longer blocked.`,
                );
                props.user.is_blocked = false;
                props.user.is_processing = true;
                props.user.is_processing_text = `${props.user.presentation_name} is no longer blocked.`;
            })
            .catch((error) => console.error(error))
            .finally(() => {
                router.reload();
            });
    } else {
        // Block
        blockUser(props.user.uuid)
            .then(() => {
                modalogStore.closeModalog();

                props.user.is_blocked = true;
                props.user.is_processing = true;
                props.user.is_processing_text = `${props.user.presentation_name} is now blocked from viewing your wish lists.`;

                if (props.redirectAfterBlocking) {
                    // Full page load ahead — park the toast for the next mount.
                    toastStore.flashNext(
                        "success",
                        `${props.user.presentation_name} is now blocked.`,
                    );
                    window.location = "/";
                } else {
                    toastStore.success(
                        `${props.user.presentation_name} is now blocked.`,
                    );
                }
            })
            .catch((error) => console.error(error))
            .finally(() => {
                router.reload();
            });
    }
}
</script>

<template>
    <BrandConfirmModalog
        :title="`${actionToUppercase} ${user.presentation_name}`"
        confirm-label="Ok"
        :busy-label="`${actionToUppercase}ing`"
        :danger="action === 'block'"
        :busy="isSubmitting"
        @confirm="handleClick"
    >
        <p v-if="action === 'block'">
            Are you sure you want to block
            <strong>{{ user.presentation_name }} (@{{ user.username }})</strong
            >? They will not be able to view your wish lists once they are
            blocked.
        </p>
        <p v-else>
            Are you sure you want to unblock
            <strong>{{ user.presentation_name }} (@{{ user.username }})</strong
            >? They will be able to view your wish lists once they are
            unblocked.
        </p>

        <a
            href="https://support.gifthero.com/page/sharing-and-connecting#how-does-user-blocking-work"
            title="How does user blocking work?"
            target="_blank"
            rel="noopener"
            class="mt-4 inline-flex items-center gap-2 text-[14px] font-semibold text-brand-navy underline underline-offset-2 transition hover:text-brand-red"
        >
            <IconHint class="size-4 shrink-0" aria-hidden="true" />
            How does user blocking work?
        </a>
    </BrandConfirmModalog>
</template>
